import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Snackbar, Alert, IconButton } from '@mui/material'
import { CheckCircleOutline, Close } from '@mui/icons-material'

// Component scope - Constants and static data
const TOAST_TYPES = ['success', 'error', 'warning', 'info']
const DEFAULT_DURATION = 4000

/**
 * App Toast Component - Reusable toast notification
 * @component
 * @param {Object} props - Component props
 * @returns {React.ReactElement} AppToastComponent
 */
const AppToastComponent = ({
  open = false,
  message = '',
  title = '',
  type = 'success',
  duration = DEFAULT_DURATION,
  onClose,
  ...props
}) => {
  // States, refs, selectors, RTK Query hooks
  const [isOpen, setIsOpen] = useState(open)

  // useEffect hooks
  useEffect(() => {
    setIsOpen(open)
  }, [open])

  useEffect(() => {
    if (isOpen && duration > 0) {
      const timer = setTimeout(() => {
        handleClose()
      }, duration)
      return () => clearTimeout(timer)
    }
  }, [isOpen, duration])

  // Other functions and logic
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setIsOpen(false)
    onClose?.(event, reason)
  }

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleOutline sx={{ color: '#FFFFFF' }} />
      case 'error':
        return <Close sx={{ color: '#EA5455' }} />
      case 'warning':
        return <CheckCircleOutline sx={{ color: '#FF9F43' }} />
      case 'info':
        return <CheckCircleOutline sx={{ color: '#00CFE8' }} />
      default:
        return <CheckCircleOutline sx={{ color: '#FFFFFF' }} />
    }
  }

  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return '#16192C'
      case 'error':
        return '#16192C'
      case 'warning':
        return '#16192C'
      case 'info':
        return '#16192C'
      default:
        return '#16192C'
    }
  }

  // Return/rendering
  return (
    <Snackbar
      open={isOpen}
      autoHideDuration={duration}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{
        '& .MuiSnackbar-root': {
          top: '24px',
          right: '24px'
        }
      }}
      {...props}
    >
      <Alert
        onClose={handleClose}
        severity={type}
        icon={getIcon()}
        sx={{
          backgroundColor: getBackgroundColor(),
          color: '#FFFFFF',
          borderRadius: '8px',
          minWidth: '400px',
          '& .MuiAlert-message': {
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: 400
          },
          '& .MuiAlert-icon': {
            color: '#FFFFFF'
          },
          '& .MuiAlert-action': {
            paddingLeft: '8px'
          }
        }}
        action={
          <IconButton
            size="small"
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: '#9E9E9E',
              padding: '0',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#9E9E9E'
              }
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {title && (
            <div style={{ fontWeight: 600, fontSize: '14px' }}>{title}</div>
          )}
          {message && <div>{message}</div>}
        </div>
      </Alert>
    </Snackbar>
  )
}

AppToastComponent.propTypes = {
  open: PropTypes.bool,
  message: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.oneOf(TOAST_TYPES),
  duration: PropTypes.number,
  onClose: PropTypes.func
}

export default AppToastComponent
